<template>
  <nav class="vf-header">
    <div class="vf-header__menu-container">
      <ul class="vf-header__menu-list vf-header__menu-level-1">
        <router-link
          :to="{ name: Route.PROJECTS_LIST }"
          class="vf-header__logo-desktop"
        >
          <img src="../../assets/BEW_logo.svg" />
          <p>{{ t('navBar.header') }}</p>
        </router-link>

        <div id="vf-header-item__container" class="vf-header-item__container">
          <div
            id="vf_header_level_1"
            :class="{
              'vf-header__item vf-header__item-1': true,
              active: currentAppSection === Route.PROJECTS_LIST,
            }"
          >
            <router-link
              id="vf-header__link"
              :to="{ name: Route.PROJECTS_LIST }"
              :class="{
                active: currentAppSection === Route.PROJECTS_LIST,
              }"
              >{{ t('navBar.projects') }}</router-link
            >
          </div>

          <div
            v-if="isUserRole(digitalizationProjectsAllowedRoles)"
            id="vf_header_level_1"
            :class="{
              'vf-header__item vf-header__item-1': true,
              active: currentAppSection === Route.DIGITALIZATION_PROJECTS_LIST,
            }"
          >
            <router-link
              id="vf-header__link"
              :to="{ name: Route.DIGITALIZATION_PROJECTS_LIST }"
              :class="{
                active:
                  currentAppSection === Route.DIGITALIZATION_PROJECTS_LIST,
              }"
              >{{ t('navBar.digitalizationProjects') }}</router-link
            >
          </div>

          <div
            v-if="canAccessWorkspaces"
            id="vf_header_level_1"
            :class="{
              'vf-header__item vf-header__item-1': true,
              active: currentAppSection === Route.WORKSPACES_LIST,
            }"
          >
            <router-link
              id="vf-header__link"
              :to="{ name: Route.WORKSPACES_LIST }"
              :class="{
                active: currentAppSection === Route.WORKSPACES_LIST,
              }"
              >{{ t('navBar.workspaces') }}</router-link
            >
          </div>

          <div
            v-if="isUserRole(suppliersManagementAllowedRoles)"
            id="vf_header_level_1"
            :class="{
              'vf-header__item vf-header__item-1': true,
              active: currentAppSection === Route.SUPPLIERS_LIST,
            }"
          >
            <router-link
              id="vf-header__link"
              :to="{ name: Route.SUPPLIERS_LIST }"
              :class="{
                active: currentAppSection === Route.SUPPLIERS_LIST,
              }"
              >{{ t('navBar.suppliers') }}</router-link
            >
          </div>
        </div>

        <div id="vf-icon-header" class="vf-icon-div">
          <div
            v-if="isUserRole([adminRole])"
            id="vf_header_level_1"
            :class="{
              'vf-header__item vf-header__item-1': true,
              active: currentAppSection === Route.ADMINISTRATION,
            }"
          >
            <router-link
              id="vf-header__link"
              :to="{ name: Route.ADMINISTRATION }"
              :class="{
                active: currentAppSection === Route.ADMINISTRATION,
              }"
              >{{ t('navBar.administration') }}</router-link
            >
          </div>
          <div id="repeat-tour-icon" v-tooltip.bottom="t('tours.repeat')">
            <font-awesome-icon
              v-if="currentTourKey"
              :icon="faSignsPost"
              @click="startTour(currentTourKey, true)"
            />
          </div>
          <div id="language-selection">
            <font-awesome-icon :icon="['fas', 'globe']" />
            <a
              id="de"
              href="#"
              :class="$store.state.language === 'de' ? 'active' : null"
              @click.prevent="setLang('de')"
            >
              de
            </a>
            <a
              id="en"
              href="#"
              :class="$store.state.language === 'en' ? 'active' : null"
              @click.prevent="setLang('en')"
            >
              en
            </a>
          </div>
          <VDropdown>
            <font-awesome-icon id="current-user-icon" :icon="['fas', 'user']" />
            <template #popper>
              <user-info />
            </template>
          </VDropdown>
          <a
            v-if="isUserRole([Role.Admin, Role.DocuResponsible])"
            :href="DOCU_RESPONSIBLE_HELP_PAGE_URL"
            target="”_blank”"
          >
            <div v-tooltip.bottom="t('navBar.docuResponsibleHelpPage')">
              <font-awesome-icon :icon="['fas', 'circle-info']" />
            </div>
          </a>
          <a
            v-if="isUserRole([Role.Admin, Role.DigitalizationCoworker])"
            :href="DIGITALIZATION_COWORKER_HELP_PAGE_URL"
            target="”_blank”"
          >
            <div v-tooltip.bottom="t('navBar.digitalizationCoworkerHelpPage')">
              <font-awesome-icon :icon="['fas', 'circle-info']" />
            </div>
          </a>

          <current-version />

          <font-awesome-icon
            id="vf-logout-icon"
            :icon="['fas', 'arrow-right-from-bracket']"
            @click="logout"
            @keyup.enter="logout"
          />
        </div>
      </ul>
    </div>
  </nav>
</template>

<script setup lang="ts">
  import { ref, computed, watch, onMounted, nextTick } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { i18n } from '../../vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import { useRoles } from '@/composables/useRoles';
  import CurrentVersion from '@/components/CurrentVersion/CurrentVersion.vue';
  import UserInfo from '../UserInfo/UserInfo.vue';
  import { Role } from 'types';
  import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
  import { getRootNodeForRoute } from '../BreadCrumb/breadCrumbHierarchy';
  import { Route } from '@/routes';
  import { usePrimeVue } from 'primevue';
  import { getLocale } from '@/primeVue/locale/getLocale';
  import { faSignsPost } from '@fortawesome/pro-regular-svg-icons';
  import { useTour } from '@/composables/useTour';

  const DOCU_RESPONSIBLE_HELP_PAGE_URL =
    'https://vattenfall.sharepoint.com/:u:/r/sites/Waerme-Berlin-Anlagen-DMS-prod/daisy-wissen/SitePages/DDP_Hanbuch_Intern_Dokumentationsverantwortliche.aspx?csf=1&web=1&e=bYi6eb';
  const DIGITALIZATION_COWORKER_HELP_PAGE_URL =
    'https://vattenfall.sharepoint.com/:u:/r/sites/Waerme-Berlin-Anlagen-DMS-prod/daisy-wissen/SitePages/DDP_Hanbuch_Intern_Lieferant.aspx?csf=1&web=1&e=my7Tl7';

  const { t } = useI18n();
  const router = useRouter();
  const store = useStore();
  const { canAccessWorkspaces, isUserRole } = useRoles();
  const primeVue = usePrimeVue();
  const { startTour } = useTour();
  const route = useRoute();

  const suppliersManagementAllowedRoles = ref([Role.Admin, Role.DocuManager]);
  const digitalizationProjectsAllowedRoles = ref([
    Role.Admin,
    Role.DocuResponsible,
    Role.DigitalizationCoworker,
  ]);
  const adminRole = ref(Role.Admin);
  const forceCurrectAppSectionUpdate = ref(0);
  const currentTourKey = ref();

  const currentAppSection = computed(() => {
    return getRootNodeForRoute(router.currentRoute.value) ?? '';
  });

  function setLang(lang: 'de' | 'en') {
    window.localStorage.setItem('lang', lang);
    i18n.global.locale = lang;
    primeVue.config.locale = getLocale();
    store.commit('setLang', lang);
  }

  function logout() {
    window.location.href = '/api/oauth/logout';
  }

  watch(
    () => router.currentRoute.value.name,
    () => {
      forceCurrectAppSectionUpdate.value++;
    },
  );

  watch(
    () => route.meta.tourKey,
    () => {
      currentTourKey.value = route.meta.tourKey;
    },
  );

  onMounted(() => {
    nextTick(() => {
      forceCurrectAppSectionUpdate.value++;
    });
  });
</script>

<style scoped lang="scss">
  nav.vf-header {
    top: 0;
    left: 0;
    height: $nav-bar-height;
    align-items: center;

    div.vf-header__menu-container {
      height: 100%;
      padding: 0 80px;
      position: static;
      margin: 0;
      display: flex;
      align-items: center;

      a.vf-header__logo-desktop {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        font-weight: 500;

        &:hover {
          text-decoration: none;
        }

        img {
          margin-top: -10px;
          height: 61px;
        }

        p {
          text-transform: uppercase;
          font-size: 17px;
          margin-top: -18px;
          font-family: 'VattenfallHallDisplay-Bold';
          margin-bottom: 0;
        }
      }

      ul.vf-header__menu-level-1 {
        height: 100%;
        width: 100%;
        grid-template-columns: auto 0.2fr 1.5fr 0.4fr;

        div#vf_header_level_1 {
          &.active {
            background-color: $app-background-color;
            margin-top: 5px;
            height: calc($nav-bar-height - 5px);
          }

          a.vf-header__logo-desktop {
            justify-self: flex-start;
          }
        }

        a#vf-header__link {
          font-weight: 500;

          &.active {
            position: relative;
            top: -5px;
          }
        }

        div.vf-header-item__container {
          height: 100%;
          justify-self: flex-start;
          grid-column: 3/4;

          div#vf_header_level_1 {
            padding: 35px;
          }
        }

        div.vf-icon-div {
          height: 100%;
          width: 100%;
          justify-self: center;
          column-gap: 50px;

          div#vf_header_level_1 {
            padding: 0;

            &.active {
              padding: 35px;
            }
          }

          svg,
          img {
            cursor: pointer;
            width: 20.25px;
            height: 20.25px;
            user-select: none;
          }

          #language-selection {
            display: flex;
            align-items: center;

            img {
              width: 27px;
              height: 27px;
              cursor: auto;
            }

            & > * {
              padding: 0 5px;
              user-select: none;
            }

            & > *:not(:first-child):not(:last-child) {
              border-right: 1px solid $border-color;
            }

            & > *:not(:first-child) {
              cursor: pointer;

              &:hover {
                color: inherit;
                text-decoration: inherit;
                font-weight: 700;
              }

              &.active {
                font-weight: 700;
              }
            }
          }
        }
      }
    }
  }
</style>
